//
/* GS://PROTOCOLO
 * Arquivo: src/components/forms/AddAccountReceivableForm.jsx
 * Versão: 2.0 – Inclusão de Categoria de Receita
 * Objetivo: Garantir classificação correta das entradas.
 */

import React, { useState, useEffect } from 'react';
import { addAccountReceivable, listUsers, getFinancialCategories } from '../../services/firestoreService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Timestamp, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const AddAccountReceivableForm = ({ onClose, onAccountAdded }) => {
    const [description, setDescription] = useState('');
    const [amountInput, setAmountInput] = useState(''); 
    const [amountRaw, setAmountRaw] = useState(0);
    const [dueDate, setDueDate] = useState('');
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
    const [customerId, setCustomerId] = useState('');
    const [categoryId, setCategoryId] = useState(''); // NOVO
    const [status, setStatus] = useState('pending');
    const [notes, setNotes] = useState('');
    
    const [customers, setCustomers] = useState([]);
    const [categories, setCategories] = useState([]); // NOVO
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [usersData, catData] = await Promise.all([
                    listUsers(),
                    getFinancialCategories()
                ]);
                // listUsers retorna { users: [] } ou array direto dependendo da impl. Vamos tratar.
                const userList = Array.isArray(usersData) ? usersData : (usersData.users || []);
                setCustomers(userList);
                
                // Filtra apenas categorias de RECEITA (income)
                setCategories(catData.filter(c => c.type === 'income'));
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
                toast.error("Erro ao carregar dados.");
            } finally {
                setLoadingData(false);
            }
        };
        loadData();
    }, []);

    const handleAmountChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); 
        let cents = parseInt(value, 10);
        if (isNaN(cents)) cents = 0;
        const paddedCents = String(cents).padStart(3, '0'); 
        const integerPart = paddedCents.slice(0, -2);
        const decimalPart = paddedCents.slice(-2);
        setAmountInput(`${parseInt(integerPart, 10).toLocaleString('pt-BR')},${decimalPart}`);
        setAmountRaw(cents);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalAmount = amountRaw / 100;

        if (!description || !finalAmount || !dueDate || !issueDate || !customerId || !categoryId) {
            toast.warning("Preencha todos os campos obrigatórios.");
            return;
        }

        const newAccount = {
            description,
            amount: finalAmount,
            dueDate: Timestamp.fromDate(new Date(dueDate)),
            issueDate: Timestamp.fromDate(new Date(issueDate)),
            customerRef: doc(db, 'users', customerId), // Referência ao cliente
            categoryRef: doc(db, 'financial_categories', categoryId), // Referência à categoria
            status,
            notes,
            // Campos de controle
            receiptDate: null,
            receivedAmount: 0,
        };

        try {
            await addAccountReceivable(newAccount);
            toast.success("Conta a receber criada com sucesso!");
            onAccountAdded();
            onClose();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error(`Erro: ${error.message}`);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6 border-b pb-2">
                <h2 className="text-xl font-bold text-gray-800">Nova Conta a Receber</h2>
                <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 font-bold text-xl">&times;</button>
            </div>

            {loadingData ? (
                <p className="text-center py-4">Carregando dados...</p>
            ) : (
                <div className="space-y-4">
                    {/* Descrição */}
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Descrição *</label>
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2 text-gray-700 focus:outline-none focus:border-brand-gold"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            required
                        />
                    </div>

                    {/* Valor e Data */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Valor (R$) *</label>
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2 text-gray-700 font-mono"
                                value={amountInput}
                                onChange={handleAmountChange}
                                placeholder="0,00"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Vencimento *</label>
                            <input
                                type="date"
                                className="w-full border rounded px-3 py-2 text-gray-700"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* Cliente e Categoria */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Cliente *</label>
                            <select
                                className="w-full border rounded px-3 py-2 text-gray-700 bg-white"
                                value={customerId}
                                onChange={(e) => setCustomerId(e.target.value)}
                                required
                            >
                                <option value="">Selecione...</option>
                                {customers.map(c => (
                                    <option key={c.uid || c.id} value={c.uid || c.id}>
                                        {c.name || c.displayName || c.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Categoria (Receita) *</label>
                            <select
                                className="w-full border rounded px-3 py-2 text-gray-700 bg-white"
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value)}
                                required
                            >
                                <option value="">Selecione...</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Data Emissão e Status */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Emissão</label>
                            <input
                                type="date"
                                className="w-full border rounded px-3 py-2 text-gray-700"
                                value={issueDate}
                                onChange={(e) => setIssueDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Status</label>
                            <select
                                className="w-full border rounded px-3 py-2 text-gray-700 bg-white"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="pending">Pendente</option>
                                <option value="received">Recebido</option>
                            </select>
                        </div>
                    </div>

                    {/* Observações */}
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Observações</label>
                        <textarea
                            className="w-full border rounded px-3 py-2 text-gray-700"
                            rows="2"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        ></textarea>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-brand-gold text-brand-black rounded hover:bg-yellow-500 font-bold"
                        >
                            Salvar Recebível
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};

export default AddAccountReceivableForm;